import { useMemo } from 'react'

import type { ColDef } from 'ag-grid-enterprise'
import { useIntl } from 'react-intl'

import {
  EnumBackedColumns,
  getAttributeDisplay,
  getAttributeOrder,
  isMobileAttributeOrder,
  sortableAttributeKeysByApi,
  toFieldPath,
} from '@b-stock/fe-core/bstock-next/components'
import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'

import { CellClass } from './cellStyles'
import { columnSpecs } from './columnSpecs'
import type { ColumnDefinition } from './types'
import {
  buildUnknownCellTooltipValueGetter,
  buildUnknownCellValueFormatter,
  UnknownCellRenderer,
} from './unknownCell'

export const defaultColDef: ColumnDefinition = {
  cellRenderer: UnknownCellRenderer,
  maxWidth: 240, // limits auto-size width
  resizable: false,
  sortable: false,
  suppressHeaderMenuButton: true,
  unSortIcon: true,
}

const sortableColIds: ReadonlySet<string> = new Set(
  sortableAttributeKeysByApi.orderProcess.map(toFieldPath)
)

export function getPinnedFieldPath(
  lot: OrchestratedListingDetails['lot']
): string {
  const attributeOrder = getAttributeOrder({
    enableStandardizedOrder: true,
    lot,
  })
  const pinnedAttributeKey = isMobileAttributeOrder(attributeOrder)
    ? 'item.attributes.model'
    : 'item.attributes.description'
  return toFieldPath(pinnedAttributeKey)
}

/**
 * Column defs must maintain object identity to prevent table state reset.
 *
 * When changing column order, widths, visibility, or pinning, bump the
 * `version` in FullManifestPanel's `useAgGridMemory` call to force-reset
 * persisted state. Without this, clients will load stale column layouts.
 */
export function useColumnDefs({
  currency,
  isLoggedIn,
  isMobileAtMount = true,
  lot,
}: {
  currency?: string
  isLoggedIn: boolean
  isMobileAtMount?: boolean
  lot: OrchestratedListingDetails['lot']
}): ColDef[] {
  const intl = useIntl()

  return useMemo(() => {
    const attributeDisplay = getAttributeDisplay({ lot })
    const attributeOrder = getAttributeOrder({
      enableStandardizedOrder: true,
      lot,
    })
    const pinnedColId = getPinnedFieldPath(lot)

    return attributeOrder.map((attributeKey) => {
      const display = attributeDisplay[attributeKey]
      const field = toFieldPath(attributeKey)
      const columnSpec = columnSpecs[field] ?? {}
      const { colDef, options } = columnSpec
      const { showTooltip } = options ?? {}

      return {
        ...(field === pinnedColId && {
          lockPinned: true,
          lockVisible: true,
          pinned: isMobileAtMount ? null : ('left' as const),
          suppressMovable: true,
        }),
        cellRendererParams: { attributeKey, currency, display, intl },
        field,
        headerName:
          display?.label ??
          intl.formatMessage({
            id: `BStockNext.ManifestTable.column.${attributeKey}`,
            defaultMessage: attributeKey,
          }),
        sortable: isLoggedIn && sortableColIds.has(field),
        tooltipValueGetter: showTooltip
          ? buildUnknownCellTooltipValueGetter({
              attributeKey,
              currency,
              display,
              intl,
            })
          : undefined,
        valueFormatter: buildUnknownCellValueFormatter({
          attributeKey,
          currency,
          display,
          intl,
        }),
        ...colDef,
        cellClass: getCellClass({ attributeKey, colDef }),
      }
    })
  }, [currency, intl, isLoggedIn, isMobileAtMount, lot])
}

// Inject CellClass.Enum for EnumBackedColumns.
// If cellClass is defined, do not override it.
const getCellClass = ({
  attributeKey,
  colDef,
}: {
  attributeKey: string
  colDef: Partial<ColumnDefinition> | undefined
}): ColDef['cellClass'] => {
  const cellClass = colDef?.cellClass
  if (cellClass) return cellClass
  if (attributeKey in EnumBackedColumns) return [CellClass.Enum]
  return undefined
}
